import React, { useCallback, useMemo, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import heic2any from 'heic2any';

// Define styles for the dropzone component
const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    minHeight: '40vh',
    maxWidth: '800px',
    marginLeft: 'auto',
    marginRight: 'auto',
    borderWidth: 2,
    borderRadius: 2,
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#000000',
    outline: 'none',
    transition: 'border .24s ease-in-out',
};

const focusedStyle = {
    borderColor: '#2196f3',
};

const acceptStyle = {
    borderColor: '#00e676',
};

const rejectStyle = {
    borderColor: '#ff1744',
};

// onError reports upward instead of calling alert(): a blocking dialog makes
// the whole tab unresponsive, and these are all recoverable — the user just
// needs to pick a different file.
const ImageDrop = ({ imagePreview, handleImageDrop, onError }) => {
    // True while heic2any runs: without it the dropzone stays interactive
    // during conversion and a second drop races the first (two uploads).
    const [converting, setConverting] = useState(false);

    const onDrop = useCallback((acceptedFiles) => {
        const file = acceptedFiles[0];

        // react-dropzone still fires onDrop with an empty accepted list when
        // every dropped file was rejected.
        if (!file) {
            return;
        }

        if (file.size > 25 * 1024 * 1024) {
            onError("Image is too large (max 25MB). Please use a smaller image.");
            return;
        }

        if (file.type === 'image/heic') {
            setConverting(true);

            heic2any({
                blob: file,
                toType: "image/jpeg",
                quality: 0.5
            })
                .then((jpegBlob) => {
                    const convertedFile = new File([jpegBlob], file.name.split('.').slice(0, -1).join('.') + ".jpeg", { type: "image/jpeg" });
                    setConverting(false);
                    handleImageDrop(convertedFile);
                })
                .catch((e) => {
                    console.log(e);
                    setConverting(false);
                    onError("Couldn't process this HEIC image — please try a JPEG or PNG.");
                })
        } else {
            handleImageDrop(file);
        }
    }, [handleImageDrop, onError]);

    useEffect(() => {
        return () => {
            // Cleanup function to cancel any ongoing tasks or subscriptions
            // if needed. Add cleanup code here if necessary.
        };
    }, []);

    const onDropRejected = useCallback((fileRejections) => {
        onError("Only JPEG, PNG, and HEIC files are allowed");
    }, [onError]);

    // Get the props and styles from the useDropzone hook
    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        disabled: converting,
        accept: {
            'image/jpeg': [],
            'image/png': [],
            'image/heic': [],
        }
    });

    // Memoized style object for the dropzone component
    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isFocused, isDragAccept, isDragReject, isDragActive]
    );

    return (
        <div className={'align-items-center justify-content-center h-100' + (imagePreview ? " d-none" : " d-flex")} {...getRootProps({ style })}>
            <input {...getInputProps()} />
            <div className="text-center">
                {/* Inline SVG so the icon rides the bundle — the drop zone
                    must render before any image/CDN request could. */}
                <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    style={{ opacity: 0.45, marginBottom: '12px' }}
                >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                </svg>
                {converting ? (
                    <p>Converting image…</p>
                ) : isDragActive ? (
                    <p>Drop the files here ...</p>
                ) : (
                    <p>Drag and Drop an Image here, or click to select files</p>
                )}
            </div>
        </div>
    );
};

export default ImageDrop;
